import React, { useState, useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import workstationService from "../services/workstation.service";
import { toast } from "react-toastify";
import EventBus from "../common/EventBus";

const WorkstationManagerView = () => {
  const [loading, setLoading] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [responseCode, setResponseCode] = useState(400);
  const [workstations, setWorkstations] = useState([]);
  const [selectedWorkstations, setSelectedWorkstations] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({});
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const { user: currentUser } = useSelector((state) => state.auth);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Get clientId from URL parameter
    const clientIdFromUrl = searchParams.get("clientId");
    if (clientIdFromUrl) {
      setClientId(clientIdFromUrl);
      // Try to get client name from URL if provided
      const clientNameFromUrl = searchParams.get("clientName");
      if (clientNameFromUrl) {
        setClientName(clientNameFromUrl);
      }
    } else {
      setResponseCode(400);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!currentUser || !clientId) return;
    
    const fetchWorkstations = async () => {
      setLoading(true);
      try {
        const response = await workstationService.getWorkstations(
          clientId,
          currentPage,
          pageSize
        );
        setWorkstations(response.data.content);
        setPagination({
          totalElements: response.data.totalElements,
          totalPages: response.data.totalPages,
          currentPage: response.data.number,
          pageSize: response.data.size,
          isFirst: response.data.first,
          isLast: response.data.last
        });
        setResponseCode(200);
      } catch (error) {
        const errorMessage =
          (error.response && error.response.data) ||
          error.message ||
          error.toString();
        console.log(error);
        setResponseCode(error.response?.status || 500);
        EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
      } finally {
        setLoading(false);
      }
    };

    fetchWorkstations();
  }, [clientId, currentPage, pageSize, currentUser]);

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (!clientId) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          Client ID is required. Please provide a clientId parameter in the URL.
        </div>
      </div>
    );
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSelectedWorkstations([]);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setSelectedWorkstations([]);
  };

  const handleSelectWorkstation = (workstationId) => {
    setSelectedWorkstations(prev => 
      prev.includes(workstationId) 
        ? prev.filter(id => id !== workstationId)
        : [...prev, workstationId]
    );
  };

  const handleSelectAll = () => {
    if (selectedWorkstations.length === workstations.length) {
      setSelectedWorkstations([]);
    } else {
      setSelectedWorkstations(workstations.map(w => w.id));
    }
  };

  const handleBatchDelete = async () => {
    if (selectedWorkstations.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedWorkstations.length} workstation(s)?`)) {
      return;
    }

    setDeleteLoading(true);
    try {
      await workstationService.batchDeleteWorkstations(clientId, selectedWorkstations);
      toast.success("Workstations deleted successfully");
      setSelectedWorkstations([]);
      // Refresh the list
      const response = await workstationService.getWorkstations(
        clientId,
        currentPage,
        pageSize
      );
      setWorkstations(response.data.content);
      setPagination({
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        currentPage: response.data.number,
        pageSize: response.data.size,
        isFirst: response.data.first,
        isLast: response.data.last
      });
    } catch (error) {
      const errorMessage = error.response?.data || error.message || "Failed to delete workstations";
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
      console.error("Batch delete failed:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteSingle = async (workstationId) => {
    if (!window.confirm("Are you sure you want to delete this workstation?")) {
      return;
    }

    setDeleteLoading(true);
    try {
      await workstationService.deleteWorkstation(clientId, workstationId);
      toast.success("Workstation deleted successfully");
      // Refresh the list
      const response = await workstationService.getWorkstations(
        clientId,
        currentPage,
        pageSize
      );
      setWorkstations(response.data.content);
      setPagination({
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        currentPage: response.data.number,
        pageSize: response.data.size,
        isFirst: response.data.first,
        isLast: response.data.last
      });
    } catch (error) {
      const errorMessage = error.response?.data || error.message || "Failed to delete workstation";
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
      console.error("Delete failed:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleDisable = async (workstationId) => {
    setDeleteLoading(true);
    try {
      await workstationService.toggleDisableWorkstation(clientId, workstationId);
      toast.success("Workstation status updated successfully");
      // Refresh the list
      const response = await workstationService.getWorkstations(
        clientId,
        currentPage,
        pageSize
      );
      setWorkstations(response.data.content);
      setPagination({
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        currentPage: response.data.number,
        pageSize: response.data.size,
        isFirst: response.data.first,
        isLast: response.data.last
      });
    } catch (error) {
      const errorMessage = error.response?.data || error.message || "Failed to update workstation status";
      EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: errorMessage });
      console.error("Toggle disable failed:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div>
      <div className="container mt-3">
        {clientName && (
          <h3>Workstations for {clientName}</h3>
        )}
        <div className="d-flex">
          <span className="text-nowrap me-2">Client ID:</span>
          <span>{clientId}</span>
        </div>
      </div>

      {responseCode >= 400 && responseCode !== 200 && (
        <div className="container mt-3">
          <div className="alert alert-danger" role="alert">
            {responseCode === 401 
              ? "Not authorized to access workstations for this client"
              : responseCode === 404
              ? "Client not found"
              : "An error occurred while loading workstations"
            }
          </div>
        </div>
      )}

      {/* Batch Actions */}
      {selectedWorkstations.length > 0 && (
        <div className="container mt-3 mb-3">
          <div className="alert alert-info d-flex justify-content-between align-items-center">
            <span>{selectedWorkstations.length} workstation(s) selected</span>
            <button 
              onClick={handleBatchDelete}
              disabled={deleteLoading}
              className="btn btn-danger"
            >
              {deleteLoading ? 'Deleting...' : 'Delete Selected'}
            </button>
          </div>
        </div>
      )}

      {/* Page Size Selector */}
      <div className="container mt-3 mb-3">
        <div className="row">
          <div className="col-md-6">
            <label className="form-label">Items per page:</label>
            <select 
              value={pageSize} 
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="form-select"
              style={{width: 'auto', display: 'inline-block'}}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Workstation Table */}
      <div className="row mt-3">
        <div className="col-12">
          <table className="table table-striped">
            <thead className="table-dark">
              <tr>
                <th scope="col">
                  <input 
                    type="checkbox" 
                    checked={selectedWorkstations.length === workstations.length && workstations.length > 0}
                    onChange={handleSelectAll}
                    className="form-check-input"
                  />
                </th>
                <th scope="col">ID</th>
                <th scope="col">Name</th>
                <th scope="col">Location</th>
                <th scope="col">Created</th>
                <th scope="col">Updated</th>
                <th scope="col">Last Login</th>
                <th scope="col">IP Address</th>
                <th scope="col">Subscription</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {workstations && workstations.length > 0 ? (
                workstations.map((workstation) => (
                  <tr key={workstation.id}>
                    <td>
                      <input 
                        type="checkbox" 
                        checked={selectedWorkstations.includes(workstation.id)}
                        onChange={() => handleSelectWorkstation(workstation.id)}
                        className="form-check-input"
                      />
                    </td>
                    <td>{workstation.id}</td>
                    <td>{workstation.name}</td>
                    <td>{workstation.location}</td>
                    <td>{new Date(workstation.createdAt).toLocaleDateString()}</td>
                    <td>{new Date(workstation.updatedAt).toLocaleDateString()}</td>
                    <td>
                      {workstation.lastLogin 
                        ? new Date(workstation.lastLogin).toLocaleDateString()
                        : 'Never'
                      }
                    </td>
                    <td>
                      {workstation.ipAddress || <span className="text-muted">—</span>}
                    </td>
                    <td>
                      {workstation.subscription || 'No subscription'}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button 
                          onClick={() => handleToggleDisable(workstation.id)}
                          disabled={deleteLoading}
                          className={`btn btn-sm ${workstation.disabled ? 'btn-success' : 'btn-warning'}`}
                          title={workstation.disabled ? 'Enable workstation' : 'Disable workstation'}
                        >
                          {workstation.disabled ? 'Enable' : 'Disable'}
                        </button>
                        <button 
                          onClick={() => handleDeleteSingle(workstation.id)}
                          disabled={deleteLoading}
                          className="btn btn-sm btn-outline-danger"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center">
                    {loading ? 'Loading workstations...' : 'No workstations found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalElements != null && (
        <div className="container mt-3">
          <nav aria-label="Workstation pagination">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                {pagination.totalPages > 1 && (
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={pagination.isFirst}
                    className="btn btn-outline-primary"
                  >
                    Previous
                  </button>
                )}
              </div>
              
              <div className="text-center">
                <span className="badge bg-secondary">
                  {pagination.totalPages > 1
                    ? <>Page {currentPage + 1} of {pagination.totalPages} ({pagination.totalElements} total workstations)</>
                    : <>{pagination.totalElements} total workstations</>}
                </span>
              </div>
              
              <div>
                {pagination.totalPages > 1 && (
                  <button 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={pagination.isLast}
                    className="btn btn-outline-primary"
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          </nav>
        </div>
      )}
    </div>
  );
};

export default WorkstationManagerView;

